import React, { useReducer, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Checkbox,
} from 'components';
import { globalPropTypes } from 'helpers';
import reducer, { defaultState, updateFieldCreator, setEditDataCreator } from './reducer';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  loadGlobalCisCertificationBodiesAttributesCreator,
  setIpmaCisEditErrorsCreator,
  updateIpmaCisCreator,
} from '../../../../../store/actionsCreators';
import DebouncedSelect from '../../../../../components/DebounceSelect/DebounceSelect';
import { getCisUsersOptions } from '../../../../../api/services/user';

const EditCertificationBody = ({
  certificationBody,
  errors,
  updateCertificationBody,
  removeError,
  countries,
  memberAssociations,
  loadCertificationBodiesAttributes,
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  useEffect(() => {
    if (certificationBody) {
      dispatch(setEditDataCreator(certificationBody));
    }
  }, [certificationBody]);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeNestedNative = (group, name, errorName) => (e) => {
    dispatch(
      updateFieldCreator({ name: group, value: { ...state[group], [name]: e.target.value } }),
    );
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeNestedSelect = (group, name, errorName) => (option) => {
    dispatch(
      updateFieldCreator({ name: group, value: { ...state[group], [name]: option?.value } }),
    );
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  useEffect(() => {
    loadCertificationBodiesAttributes();
  }, []);

  const submit = () => {
    const body = {
      country: state.country,
      initials: state.initials,
      name: state.name,
      cbHeadName: state.cbHeadName,
      cbHeadEmail: state.cbHeadEmail,
      cbHeadTelephone: state.cbHeadTelephone,
      contactName: state.contactName,
      contactEmail: state.contactEmail,
      contactTelephone: state.contactTelephone,
      vatId: state.vatId,
      memberAssociation: state.memberAssociation,
      address: {
        line1: state.address?.line1,
        line2: state.address?.line2,
        postCode: state.address?.postCode,
        city: state.address?.city,
        country: state.address?.country,
      },
      user: state.user,
      suspended: state.suspended,
      operational: state.operational,
    };

    if (state.invoiceAddressSame) {
      body.invoiceAddressSame = state.invoiceAddressSame;
    } else {
      body.invoiceAddress = {
        line1: state.invoiceAddress?.line1,
        line2: state.invoiceAddress?.line2,
        postCode: state.invoiceAddress?.postCode,
        city: state.invoiceAddress?.city,
        country: state.invoiceAddress?.country,
      };
    }

    updateCertificationBody(state.id, body, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">
              {t('page.user.cis.certificationBodies.editCertificationBody')}
            </Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="country"
                  label={t('page.user.cis.certificationBodies.country')}
                  value={countries.find((option) => option.value === state.country) || ''}
                  clear={() => handleChangeSelect('country')(null)}
                  required
                  error={errors.country}
                >
                  <Select
                    id="country"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.countryPlaceholder')}
                    value={countries.find((option) => option.value === state.country) || ''}
                    options={countries}
                    onChange={handleChangeSelect('country', 'country')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="initials"
                  label={t('page.user.cis.certificationBodies.initials')}
                  error={errors.initials}
                >
                  <input
                    id="initials"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.initialsPlaceholder')}
                    value={state.initials}
                    onChange={handleChangeNative('initials', 'initials')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="name"
                  label={t('page.user.cis.certificationBodies.name')}
                  required
                  error={errors.name}
                >
                  <input
                    id="name"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.namePlaceholder')}
                    value={state.name}
                    onChange={handleChangeNative('name', 'name')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cbHeadName"
                  label={t('page.user.cis.certificationBodies.cbHeadName')}
                  required
                  error={errors.cbHeadName}
                >
                  <input
                    id="cbHeadName"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.cbHeadNamePlaceholder')}
                    value={state.cbHeadName}
                    onChange={handleChangeNative('cbHeadName', 'cbHeadName')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cbHeadEmail"
                  label={t('page.user.cis.certificationBodies.cbHeadEmail')}
                  required
                  error={errors.cbHeadEmail}
                >
                  <input
                    id="cbHeadEmail"
                    type="email"
                    placeholder={t('page.user.cis.certificationBodies.cbHeadEmailPlaceholder')}
                    value={state.cbHeadEmail}
                    onChange={handleChangeNative('cbHeadEmail', 'cbHeadEmail')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="cbHeadTelephone"
                  label={t('page.user.cis.certificationBodies.cbHeadTelephone')}
                  required
                  error={errors.cbHeadTelephone}
                >
                  <input
                    id="cbHeadTelephone"
                    type="tel"
                    placeholder={t('page.user.cis.certificationBodies.cbHeadTelephonePlaceholder')}
                    value={state.cbHeadTelephone}
                    onChange={handleChangeNative('cbHeadTelephone', 'cbHeadTelephone')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="contactName"
                  label={t('page.user.cis.certificationBodies.contactName')}
                  error={errors.contactName}
                >
                  <input
                    id="contactName"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.contactNamePlaceholder')}
                    value={state.contactName}
                    onChange={handleChangeNative('contactName', 'contactName')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="contactEmail"
                  label={t('page.user.cis.certificationBodies.contactEmail')}
                  error={errors.contactEmail}
                >
                  <input
                    id="contactEmail"
                    type="email"
                    placeholder={t('page.user.cis.certificationBodies.contactEmailPlaceholder')}
                    value={state.contactEmail}
                    onChange={handleChangeNative('contactEmail', 'contactEmail')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="contactTelephone"
                  label={t('page.user.cis.certificationBodies.contactTelephone')}
                  error={errors.contactTelephone}
                >
                  <input
                    id="contactTelephone"
                    type="tel"
                    placeholder={t('page.user.cis.certificationBodies.contactTelephonePlaceholder')}
                    value={state.contactTelephone}
                    onChange={handleChangeNative('contactTelephone', 'contactTelephone')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="vatId"
                  label={t('page.user.cis.certificationBodies.vatId')}
                  error={errors.vatId}
                >
                  <input
                    id="vatId"
                    type="text"
                    placeholder={t('page.user.cis.certificationBodies.vatIdPlaceholder')}
                    value={state.vatId}
                    onChange={handleChangeNative('vatId', 'vatId')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="memberAssociation"
                  label={t('page.user.cis.certificationBodies.memberAssociation')}
                  value={
                    memberAssociations?.find(
                      (option) => option.value === state.memberAssociation,
                    ) || ''
                  }
                  clear={() => handleChangeSelect('memberAssociation')(null)}
                  required
                  error={errors.memberAssociation}
                >
                  <Select
                    id="memberAssociation"
                    type="text"
                    placeholder={t(
                      'page.user.cis.certificationBodies.memberAssociationPlaceholder',
                    )}
                    value={
                      memberAssociations?.find(
                        (option) => option.value === state.memberAssociation,
                      ) || ''
                    }
                    options={memberAssociations}
                    onChange={handleChangeSelect('memberAssociation', 'memberAssociation')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="user"
                  label={t('page.user.cis.certificationBodies.user')}
                  error={errors.user}
                >
                  <DebouncedSelect
                    id="user"
                    value={state.selectedUser}
                    onChange={handleChangeSelect('user', 'user')}
                    placeholder={t('page.user.cis.certificationBodies.userPlaceholder')}
                    fetchOptions={getCisUsersOptions}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="suspended"
                  label={t('page.user.cis.certificationBodies.suspended')}
                  value={state.suspended}
                  error={errors.suspended}
                >
                  <Checkbox
                    id="suspended"
                    selected={state.suspended}
                    onChange={handleChangeCustom('suspended', 'suspended')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="operational"
                  label={t('page.user.cis.certificationBodies.operational')}
                  value={state.operational}
                  error={errors.operational}
                >
                  <Checkbox
                    id="operational"
                    selected={state.operational}
                    onChange={handleChangeCustom('operational', 'operational')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>

          <Margin bottom={{ xl: 20 }} style={{ padding: '10px', border: '1px solid' }}>
            <Margin bottom="24">
              <Heading size="h5">{t('page.user.cis.organizations.address')}</Heading>
            </Margin>
            <Row>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-line1"
                    label={t('global.addressLineLabel1')}
                    required
                    error={errors.address?.line1}
                  >
                    <input
                      id="address-line1"
                      type="text"
                      placeholder={t('global.addressLinePlaceholder1')}
                      value={state.address?.line1}
                      onChange={handleChangeNestedNative('address', 'line1', 'address.line1')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-line2"
                    label={t('global.addressLineLabel2')}
                    error={errors.address?.line2}
                  >
                    <input
                      id="address-line2"
                      type="text"
                      placeholder={t('global.addressLinePlaceholder2')}
                      value={state.address?.line2}
                      onChange={handleChangeNestedNative('address', 'line2', 'address.line2')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-city"
                    label={t('global.cityPlaceholder')}
                    required
                    error={errors.address?.city}
                  >
                    <input
                      id="address-city"
                      type="text"
                      placeholder={t('global.cityPlaceholder')}
                      value={state.address?.city}
                      onChange={handleChangeNestedNative('address', 'city', 'address.city')}
                    />
                  </Field>
                </Margin>
              </Col>
            </Row>
            <Row>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-post-code"
                    label={t('global.postCodeLabel')}
                    required
                    error={errors.address?.postCode}
                  >
                    <input
                      id="address-post-code"
                      type="text"
                      placeholder={t('global.postCodePlaceholder')}
                      value={state.address?.postCode}
                      onChange={handleChangeNestedNative('address', 'postCode', 'address.postCode')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="address-country"
                    label={t('page.user.cis.organizations.country')}
                    value={
                      countries.find((option) => option.value === state.address?.country) || ''
                    }
                    clear={() =>
                      handleChangeNestedSelect('address', 'country', 'address.country')(null)
                    }
                    required
                    error={errors.address?.country}
                  >
                    <Select
                      id="address-country"
                      type="text"
                      placeholder={t('page.user.cis.organizations.countryPlaceholder')}
                      value={
                        countries.find((option) => option.value === state.address?.country) || ''
                      }
                      options={countries}
                      onChange={handleChangeNestedSelect('address', 'country', 'address.country')}
                    />
                  </Field>
                </Margin>
              </Col>
              <Col size={{ xl: 1 / 3 }}>
                <Margin bottom="20">
                  <Field
                    id="invoiceAddressSame"
                    label={t('page.user.cis.organizations.invoiceAddressSame')}
                    value={state.invoiceAddressSame}
                    error={errors.invoiceAddressSame}
                  >
                    <Checkbox
                      id="invoiceAddressSame"
                      selected={state.invoiceAddressSame}
                      onChange={handleChangeCustom('invoiceAddressSame', 'invoiceAddressSame')}
                    ></Checkbox>
                  </Field>
                </Margin>
              </Col>
            </Row>
          </Margin>
          {!state.invoiceAddressSame && (
            <Margin bottom={{ xl: 20 }} style={{ padding: '10px', border: '1px solid' }}>
              <Margin bottom="24">
                <Heading size="h5">{t('page.user.cis.organizations.invoiceAddress')}</Heading>
              </Margin>
              <Row>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-line1"
                      label={t('global.addressLineLabel1')}
                      required
                      error={errors['invoiceAddress.line1']}
                    >
                      <input
                        id="invoiceAddress-line1"
                        type="text"
                        placeholder={t('global.addressLinePlaceholder1')}
                        value={state.invoiceAddress?.line1}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'line1',
                          'invoiceAddress.line1',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-line2"
                      label={t('global.addressLineLabel2')}
                      error={errors['invoiceAddress.line2']}
                    >
                      <input
                        id="invoiceAddress-line2"
                        type="text"
                        placeholder={t('global.addressLinePlaceholder2')}
                        value={state.invoiceAddress?.line2}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'line2',
                          'invoiceAddress.line2',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-city"
                      label={t('global.cityPlaceholder')}
                      required
                      error={errors['invoiceAddress.city']}
                    >
                      <input
                        id="invoiceAddress-city"
                        type="text"
                        placeholder={t('global.cityPlaceholder')}
                        value={state.invoiceAddress?.city}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'city',
                          'invoiceAddress.city',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
              <Row>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-post-code"
                      label={t('global.postCodeLabel')}
                      error={errors['invoiceAddress.postCode']}
                    >
                      <input
                        id="invoiceAddress-post-code"
                        type="text"
                        placeholder={t('global.postCodePlaceholder')}
                        value={state.invoiceAddress?.postCode}
                        onChange={handleChangeNestedNative(
                          'invoiceAddress',
                          'postCode',
                          'invoiceAddress.postCode',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
                <Col size={{ xl: 1 / 3 }}>
                  <Margin bottom="20">
                    <Field
                      id="invoiceAddress-country"
                      label={t('page.user.cis.organizations.country')}
                      value={
                        countries.find(
                          (option) => option.value === state.invoiceAddress?.country,
                        ) || ''
                      }
                      clear={() =>
                        handleChangeNestedSelect(
                          'invoiceAddress',
                          'country',
                          'invoiceAddress.country',
                        )(null)
                      }
                      required
                      error={errors['invoiceAddress.country']}
                    >
                      <Select
                        id="invoiceAddress-country"
                        type="text"
                        placeholder={t('page.user.cis.organizations.countryPlaceholder')}
                        value={
                          countries.find(
                            (option) => option.value === state.invoiceAddress?.country,
                          ) || ''
                        }
                        options={countries}
                        onChange={handleChangeNestedSelect(
                          'invoiceAddress',
                          'country',
                          'invoiceAddress.country',
                        )}
                      />
                    </Field>
                  </Margin>
                </Col>
              </Row>
            </Margin>
          )}

          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

EditCertificationBody.propTypes = {
  certificationBody: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    country: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    memberAssociation: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    cbHeadName: PropTypes.string,
    cbHeadEmail: PropTypes.string,
    cbHeadTelephone: PropTypes.string,
    contactName: PropTypes.string,
    contactEmail: PropTypes.string,
    contactTelephone: PropTypes.string,
    address: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      postCode: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    }),
    invoiceAddress: PropTypes.shape({
      line1: PropTypes.string,
      line2: PropTypes.string,
      postCode: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    }),
    vatId: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }),
  errors: PropTypes.shape({
    name: PropTypes.string,
    country: PropTypes.string,
    memberAssociation: PropTypes.string,
    cbHeadName: PropTypes.string,
    cbHeadEmail: PropTypes.string,
    cbHeadTelephone: PropTypes.string,
    contactName: PropTypes.string,
    contactEmail: PropTypes.string,
    contactTelephone: PropTypes.string,
    address: PropTypes.string,
    invoiceAddress: PropTypes.string,
    vatId: PropTypes.string,
    user: PropTypes.string,
  }),
  updateCertificationBody: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  memberAssociations: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
  loadCertificationBodiesAttributes: PropTypes.func.isRequired,
};

EditCertificationBody.defaultProps = {
  certificationBody: {},
  errors: {},
};

const mapStateToProps = ({ ipma, globals }) => ({
  certificationBody: ipma.cis.certificationBodies.profile,
  errors: ipma.cis.certificationBodies.editErrors,
  countries: globals.countries,
  memberAssociations: globals.availableMA,
});

const mapDispatchToProps = (dispatch) => ({
  updateCertificationBody: (id, payload, history) =>
    dispatch(updateIpmaCisCreator(id, 'certificationBodies', payload, history)),
  removeError: (payload) => dispatch(setIpmaCisEditErrorsCreator('certificationBodies', payload)),
  loadCertificationBodiesAttributes: () =>
    dispatch(loadGlobalCisCertificationBodiesAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditCertificationBody);
